import { collection, addDoc, doc, updateDoc } from 'firebase/firestore';
import { db } from './firebase';
import { analyzeTeaching, generateContentVariations, generateConversationResponse } from './openai';
import { voiceService } from './services/voiceService';
import { useStore } from './store';

export const processTeaching = async (transcript: string) => {
  try {
    const { user, hasValidApiKeys } = useStore.getState();
    
    if (!hasValidApiKeys()) {
      throw new Error('API keys not configured');
    }
    
    if (!user) {
      throw new Error('User must be authenticated to save teachings');
    }
    
    const analysis = await analyzeTeaching(transcript);
    
    const teaching = {
      id: crypto.randomUUID(),
      content: transcript,
      summary: analysis,
      timestamp: new Date(),
      userId: user.uid,
      aiResponses: [],
      additionalTranscripts: []
    };
    
    const docRef = await addDoc(collection(db, `users/${user.uid}/teachings`), teaching);
    teaching.id = docRef.id;
    useStore.getState().addTeaching(teaching);
    
    return teaching;
  } catch (error) {
    console.error('Error processing teaching:', error);
    throw error;
  }
};

export const regenerateTeaching = async (teaching: any) => {
  try {
    const { hasValidApiKeys } = useStore.getState();
    
    if (!hasValidApiKeys()) {
      throw new Error('API keys not configured');
    }

    const transcriptContent = [
      teaching.content,
      ...(teaching.additionalTranscripts?.map(t => t.content) || [])
    ].join('\n\n');

    const analysis = await analyzeTeaching(transcriptContent);
    
    const user = useStore.getState().user;
    if (user && teaching.id) {
      await updateDoc(doc(db, `users/${user.uid}/teachings/${teaching.id}`), {
        summary: analysis
      });
    }

    useStore.getState().updateCurrentTeaching(analysis);
    return analysis;
  } catch (error) {
    console.error('Error regenerating teaching:', error);
    throw error;
  }
};

export const generateAIResponse = async (teaching: any) => {
  try {
    const { hasValidApiKeys } = useStore.getState();
    
    if (!hasValidApiKeys()) {
      throw new Error('Please configure your API keys first');
    }

    const transcriptContent = [
      teaching.content,
      ...(teaching.additionalTranscripts?.map(t => t.content) || [])
    ].join('\n\n');

    const response = await generateConversationResponse(transcriptContent);
    
    try {
      await voiceService.speak(response);
    } catch (error) {
      console.error('Voice service error:', error);
      // Continue even if voice fails - just return the text response
    }
    
    return response;
  } catch (error) {
    console.error('Error generating AI response:', error);
    throw error;
  }
};

export const formatContent = async (teaching: string, type: 'reel' | 'long' | 'short') => {
  try {
    const { hasValidApiKeys } = useStore.getState();
    
    if (!hasValidApiKeys()) {
      throw new Error('Please configure your API keys first');
    }

    const content = await generateContentVariations(teaching, type);
    return content;
  } catch (error) {
    console.error('Error formatting content:', error);
    throw error;
  }
};